import { useRef } from 'react';

import { css } from '@emotion/css';
import { Grid, LinearProgress, Typography } from '@mui/material';

import { FilePreview } from './FilePreview';

export const dropZoneStyle = css`
  width: 100% !important;
  border-radius: 4px;
  background: #fafafb;
  color: rgba(0, 0, 0, 0.87);
  text-transform: none;
  font-weight: 400;
  font-size: 1rem;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  cursor: pointer;
  transition: border-color 0.2s;
  &:hover,
  &.dragover {
    background-color: rgba(25, 118, 210, 0.08);
    border-color: #1976d2;
  }
`;

interface MInputFileProps {
  accept?: string[];
  file?: File;
  error?: string;
  percent?: number;
  onChange?: (file: File | null) => void;
}

export const MInputFile = ({ accept, file, percent, error, onChange }: MInputFileProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;

    if (files && files.length > 0) {
      onChange?.(files[0]);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.classList.add('dragover');
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.classList.remove('dragover');
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      onChange?.(files[0]);
    }
  };

  const handleRemoveFile = () => {
    onChange?.(null);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <Grid container>
      <Grid item md={12}>
        <div
          className={dropZoneStyle}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClick}
          tabIndex={0}
          role='button'
          aria-label='Drop a file to upload, or click to select it'>
          <span>Drop a file to upload, or click to select it.</span>
          <input
            ref={inputRef}
            type='file'
            style={{ display: 'none' }}
            onChange={handleInputChange}
            multiple={false}
            accept={accept?.join(',')}
          />
        </div>
      </Grid>
      {error && (
        <Grid item md={12}>
          <Typography variant='caption' color='error'>
            {error}
          </Typography>
        </Grid>
      )}
      {file && (
        <Grid item md={12}>
          <FilePreview file={file} onRemove={handleRemoveFile} />
        </Grid>
      )}
      {percent && percent > 0 && (
        <Grid item md={12}>
          <LinearProgress color='success' variant='determinate' value={percent} />
        </Grid>
      )}
    </Grid>
  );
};
